import type { T_JsonValue } from "@lattice/core-lib/types/chat-protocol";
import type {
  T_ChatV8ElicitationAnswer,
  T_ChatV8ElicitationField,
} from "@lattice/core-lib/types/chat-v8";
import {
  buildStockAutocompleteAnswer,
  chatV8ElicitationMachine,
  getChatV8ElicitationViewState,
  isChatV8ElicitationFormComplete,
  parseChatV8ElicitationRequest,
  shouldAutoSubmitChatV8ElicitationForm,
} from "@lattice/shared-frontend/chat-v8";
import { useMachine } from "@xstate/react";
import React, { useRef } from "react";

import { Pill } from "@lattice/frontend/components/kit/Pill";
import { TextField } from "@lattice/frontend/components/kit/TextField";
import StockSearch from "@lattice/frontend/components/workspace/stock-search";

import "../chat-v8.css";

function defaultRenderStockSearch(props: T_ElicitationStockSearchProps) {
  return <StockSearch appearance="fill" {...props} />;
}

export type T_ElicitationStockSearchProps = {
  onClearSelectedItems: () => void;
  onSearchChange: (value: string) => void;
  onSelect: (stock: { symbol: string }) => void;
  placeholder: string;
};

export type T_ElicitationChipsProps = {
  answered: T_ChatV8ElicitationAnswer | null;
  onAnswer: (answer: T_ChatV8ElicitationAnswer) => void;
  payload: unknown;
  /**
   * Overridable so stories can render the company-analysis autocomplete
   * without a tRPC provider; production uses the live stock search.
   */
  renderStockSearch?: (props: T_ElicitationStockSearchProps) => React.ReactNode;
  stockAutocomplete?: boolean;
};

export function ElicitationChips({
  answered,
  onAnswer,
  payload,
  renderStockSearch,
  stockAutocomplete = false,
}: T_ElicitationChipsProps) {
  const request = parseChatV8ElicitationRequest(payload);
  if (!request) return null;
  return (
    <ElicitationController
      answered={answered}
      onAnswer={onAnswer}
      renderStockSearch={renderStockSearch}
      request={request}
      stockAutocomplete={stockAutocomplete}
    />
  );
}

function ElicitationController({
  answered,
  onAnswer,
  renderStockSearch,
  request,
  stockAutocomplete,
}: {
  answered: T_ChatV8ElicitationAnswer | null;
  onAnswer: (answer: T_ChatV8ElicitationAnswer) => void;
  renderStockSearch?: (props: T_ElicitationStockSearchProps) => React.ReactNode;
  request: NonNullable<ReturnType<typeof parseChatV8ElicitationRequest>>;
  stockAutocomplete: boolean;
}) {
  const callbacksRef = useRef({ onAnswer });
  callbacksRef.current = { onAnswer };
  const [snapshot, send] = useMachine(chatV8ElicitationMachine, {
    input: { callbacks: callbacksRef, request },
  });
  const view = getChatV8ElicitationViewState(snapshot, answered);

  if (request.mode === "free_text" || stockAutocomplete) {
    const submitFreeText = () => {
      if (stockAutocomplete) {
        const text = view.freeText.trim();
        if (text.length === 0) return;
        send({
          answer: buildStockAutocompleteAnswer(request, text),
          type: "SUBMIT_ANSWER",
        });
        return;
      }
      send({ type: "SUBMIT_FREE_TEXT" });
    };

    return (
      <form
        className="chat-v8-elicitation chat-v8-elicitation--free-text"
        onSubmit={(event) => {
          event.preventDefault();
          submitFreeText();
        }}
      >
        <p>{request.prompt}</p>
        {stockAutocomplete ? (
          // Same production search the manual-holdings panel uses: logos,
          // combobox results, and a selected pill. Free text still submits,
          // because the model can resolve a name our search does not match.
          (renderStockSearch ?? defaultRenderStockSearch)({
            onClearSelectedItems: () =>
              send({ text: "", type: "FREE_TEXT_CHANGED" }),
            onSearchChange: (text) => send({ text, type: "FREE_TEXT_CHANGED" }),
            onSelect: (stock) =>
              send({
                answer: buildStockAutocompleteAnswer(request, stock.symbol),
                type: "SUBMIT_ANSWER",
              }),
            placeholder: "Company name or ticker",
          })
        ) : (
          <TextField
            ariaLabel={request.prompt}
            disabled={view.disabled}
            onChange={(event) =>
              send({ text: event.target.value, type: "FREE_TEXT_CHANGED" })
            }
            placeholder="Type your answer…"
            value={view.freeText}
          />
        )}
        <Pill
          disabled={view.disabled || view.freeText.trim().length === 0}
          label={stockAutocomplete ? "Analyze" : "Continue"}
          onClick={submitFreeText}
        />
      </form>
    );
  }

  if (request.mode === "multi_select") {
    return (
      <div className="chat-v8-elicitation">
        <p>{request.prompt}</p>
        <div className="chat-v8-pill-row">
          {(request.options ?? []).map((option) => (
            <Pill
              disabled={view.disabled}
              key={option.optionCuid}
              label={option.label}
              onClick={() =>
                send({
                  optionCuid: option.optionCuid,
                  type: "MULTI_OPTION_TOGGLED",
                })
              }
              selected={view.multiSelected.includes(option.optionCuid)}
            />
          ))}
        </div>
        <Pill
          disabled={
            view.disabled ||
            (request.required && view.multiSelected.length === 0)
          }
          label="Continue"
          onClick={() => send({ type: "SUBMIT_MULTI_SELECT" })}
        />
      </div>
    );
  }

  if (request.mode === "single_select") {
    return (
      <div className="chat-v8-elicitation">
        <p>{request.prompt}</p>
        <div className="chat-v8-pill-row">
          {(request.options ?? []).map((option) => (
            <Pill
              disabled={view.disabled}
              key={option.optionCuid}
              label={option.label}
              onClick={() =>
                send({
                  optionCuid: option.optionCuid,
                  type: "SINGLE_OPTION_SELECTED",
                })
              }
              selected={view.selectedOption === option.optionCuid}
            />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="chat-v8-elicitation">
      <p>{request.prompt}</p>
      {(request.fields ?? []).map((field) => renderFormField(field))}
      {!view.disabled && !shouldAutoSubmitChatV8ElicitationForm(request) ? (
        <Pill
          disabled={
            view.disabled ||
            !isChatV8ElicitationFormComplete(request, view.formFields)
          }
          label="Continue"
          onClick={() => send({ type: "SUBMIT_FORM" })}
        />
      ) : null}
    </div>
  );

  function renderFormField(field: T_ChatV8ElicitationField) {
    const value = view.formFields[field.fieldCuid];
    if (field.kind === "single_select") {
      return (
        <div className="chat-v8-elicitation__field" key={field.fieldCuid}>
          <p>{field.label}</p>
          <div className="chat-v8-pill-row">
            {(field.options ?? []).map((option) => (
              <Pill
                disabled={view.disabled}
                key={option.optionCuid}
                label={option.label}
                onClick={() =>
                  send({
                    fieldCuid: field.fieldCuid,
                    optionCuid: option.optionCuid,
                    type: "FORM_OPTION_SELECTED",
                  })
                }
                selected={value === option.optionCuid}
              />
            ))}
          </div>
        </div>
      );
    }

    if (field.kind === "multi_select") {
      const selected = getStringArray(value);
      return (
        <div className="chat-v8-elicitation__field" key={field.fieldCuid}>
          <p>{field.label}</p>
          <div className="chat-v8-pill-row">
            {(field.options ?? []).map((option) => (
              <Pill
                disabled={view.disabled}
                key={option.optionCuid}
                label={option.label}
                onClick={() =>
                  send({
                    fieldCuid: field.fieldCuid,
                    type: "FORM_FIELD_CHANGED",
                    value: selected.includes(option.optionCuid)
                      ? selected.filter((cuid) => cuid !== option.optionCuid)
                      : [...selected, option.optionCuid],
                  })
                }
                selected={selected.includes(option.optionCuid)}
              />
            ))}
          </div>
        </div>
      );
    }

    const text = typeof value === "string" ? value : "";
    return (
      <div className="chat-v8-elicitation__field" key={field.fieldCuid}>
        <TextField
          ariaLabel={field.label}
          disabled={view.disabled}
          label={field.label}
          onChange={(event) =>
            send({
              fieldCuid: field.fieldCuid,
              type: "FORM_FIELD_CHANGED",
              value: event.target.value,
            })
          }
          placeholder={field.kind === "number" ? "Enter a number" : "Type here"}
          value={text}
        />
      </div>
    );
  }
}

function getStringArray(value: T_JsonValue | undefined): string[] {
  return Array.isArray(value)
    ? value.filter((item): item is string => typeof item === "string")
    : [];
}
